import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, FileText, Download, Trash2, Shield } from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function GDPR() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/edrano-logo-dark.png" alt="Edrano" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-lg text-slate-900">Edrano</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 to-indigo-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            GDPR Compliance
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto">
            We take data protection seriously. Here's how Edrano helps you stay compliant with GDPR.
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="py-16" data-testid="gdpr-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Key Rights */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Your Rights Under GDPR</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="p-6 bg-white rounded-2xl border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Right of Access (Art. 15)</h3>
                <p className="text-slate-600 text-sm">
                  You can request a copy of all personal data we hold about you. Export your data anytime from your account settings.
                </p>
              </div>
              
              <div className="p-6 bg-white rounded-2xl border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                  <Download className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Right to Portability (Art. 20)</h3>
                <p className="text-slate-600 text-sm">
                  Receive your data in a structured, machine-readable format (JSON). Transfer your data to another service easily.
                </p>
              </div>
              
              <div className="p-6 bg-white rounded-2xl border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                  <Trash2 className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Right to Erasure (Art. 17)</h3>
                <p className="text-slate-600 text-sm">
                  Request deletion of your personal data. We'll anonymize or delete your data, subject to legal retention requirements.
                </p>
              </div>
              
              <div className="p-6 bg-white rounded-2xl border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                  <Check className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Consent Management (Art. 7)</h3>
                <p className="text-slate-600 text-sm">
                  Manage your consent preferences anytime. Withdraw consent for marketing, analytics, or other processing.
                </p>
              </div>
            </div>
          </section>

          {/* How We Comply */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">How Edrano Ensures Compliance</h2>
            
            <div className="space-y-6">
              <div className="p-6 bg-white rounded-2xl border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-3">Data Processing</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 text-sm">We only process data necessary for providing our services (data minimization)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 text-sm">All data processing is documented with lawful basis</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 text-sm">Processing records available under Article 30</span>
                  </li>
                </ul>
              </div>

              <div className="p-6 bg-white rounded-2xl border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-3">Security Measures</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 text-sm">Encryption in transit (TLS 1.3) and at rest (AES-256)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 text-sm">Secure password hashing with bcrypt</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 text-sm">Regular security audits and penetration testing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 text-sm">SOC 2 Type II compliant infrastructure</span>
                  </li>
                </ul>
              </div>

              <div className="p-6 bg-white rounded-2xl border border-slate-100">
                <h3 className="font-semibold text-slate-900 mb-3">Data Retention</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 text-sm">Data retained only as long as necessary for the purpose</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 text-sm">Automatic data cleanup for inactive accounts (after notice)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 text-sm">Financial records retained for legal compliance (7 years)</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* For Your Clients */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">GDPR for Your Clients</h2>
            <p className="text-slate-600 mb-6">
              As a data controller for your clients, Edrano provides tools to help you comply with GDPR:
            </p>
            
            <div className="p-6 bg-indigo-50 rounded-2xl border border-indigo-100">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700"><strong>Data Export API:</strong> Export client data for subject access requests</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700"><strong>Data Deletion API:</strong> Anonymize or delete client data on request</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700"><strong>Consent Tracking:</strong> Record and manage client consents</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700"><strong>Processing Records:</strong> Auto-generated Article 30 compliance documentation</span>
                </li>
              </ul>
            </div>
          </section>

          {/* DPA */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Data Processing Agreement</h2>
            <p className="text-slate-600 mb-6">
              Enterprise customers can request a Data Processing Agreement (DPA) that covers our processing of personal data on your behalf.
            </p>
            <a 
              href="mailto:legal@edrano.my?subject=DPA%20Request"
              className="inline-flex items-center gap-2 text-indigo-600 font-medium hover:text-indigo-700"
            >
              Request DPA <ArrowLeft className="w-4 h-4 rotate-180" />
            </a>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Data Protection Contact</h2>
            <div className="p-6 bg-slate-100 rounded-2xl">
              <p className="text-slate-600 mb-4">
                For GDPR-related inquiries, data subject requests, or to report a concern:
              </p>
              <p className="text-slate-700">
                <strong>Email:</strong> privacy@edrano.my<br />
                <strong>Response Time:</strong> Within 30 days (as required by GDPR)
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 bg-slate-100 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} Edrano. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-indigo-600">Privacy</Link>
              <Link to="/terms" className="text-sm text-slate-500 hover:text-indigo-600">Terms</Link>
              <Link to="/cookies" className="text-sm text-slate-500 hover:text-indigo-600">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
